// ═══════════════════════════════════════════════════════════════════════════
// drpAlertEmail.js — exactly what an agent would receive, rendered ONCE.
//
// DELIVERY IS OFF. `DECISION_ALERTS_ACTIVE` in api/_drp-signal.js is false and
// this module sends nothing; it exists so the wording can be reviewed on screen
// before anything is switched on, and so the preview and the eventual email
// cannot be two different texts. That is the whole reason it is a module rather
// than a string built inside the sender.
//
// ── WHAT THIS MAY NEVER SAY ────────────────────────────────────────────────
// "BUY DRP NOW". The engine holds no farm economics — no milk volume, no
// components, no feed cost, no margin, and (on this screen) no per-farm
// coverage. It is not entitled to tell a dairy to buy anything. The subject
// line is a REVIEW request and the body reports measured facts plus the things
// it could not evaluate, by name.
//
// Three absences are printed rather than omitted, because an alert that lists
// only what it knows reads as though it knew everything:
//   - farm margin fit (Layer B is not built)
//   - existing coverage where it is UNKNOWN or INCOMPLETE (never "none")
//   - the sales deadline (no verified RMA source is held)
// ═══════════════════════════════════════════════════════════════════════════

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const ord = (p) => {
  if (p == null) return "—";
  const k = Math.round(p * 100), s = ["th", "st", "nd", "rd"], v = k % 100;
  return `${k}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const prettyQuarter = (qkey) => (/^(\d{4})Q([1-4])$/.test(qkey || "")
  ? `${qkey.slice(0, 4)} Q${qkey.slice(5)}` : (qkey || "—"));

const STATE_NAME = { "36": "New York", "42": "Pennsylvania", "26": "Michigan" };

// One transition -> one alert. `d` is a drpDecide result; `prev` the stored
// prior decision (or null); `coverage` a drpCoverage cell result (or null).
function renderDrpAlert({ transition, decision: d, prev, coverage, stateCode }) {
  if (!d || !transition || !transition.kind) return null;

  const q = prettyQuarter(d.qkey);
  const cls = d.milkClass || "";
  const st = STATE_NAME[String(stateCode)] || String(stateCode || "");
  const fromLabel = prev && prev.floorQualityLabel ? prev.floorQualityLabel : (prev && prev.decision ? prev.decision : null);
  const toLabel = d.floorQuality ? d.floorQuality.label : null;

  // The SUBJECT is a review request. It names the coverage and what moved —
  // never an instruction, and never a dollar figure on its own, which reads as
  // a quote.
  const subject = transition.kind === "data_stale"
    ? `DRP data check — ${cls} ${q} (${st})`
    : `DRP review — ${cls} ${q} (${st})`;

  const lines = [];
  lines.push(`DRP REVIEW — ${cls} ${q} · ${st}`);
  lines.push("");
  if (transition.kind === "data_stale") {
    lines.push(`This quarter's read has been withheld: ${transition.reason || "data gate closed"}.`);
    lines.push("No decision is being offered until the board is current again.");
  } else if (fromLabel && toLabel && fromLabel !== toLabel) {
    lines.push(`Available floor quality moved from ${fromLabel} → ${toLabel}.`);
  } else if (toLabel) {
    lines.push(`Available floor quality is ${toLabel}.`);
  }
  lines.push("");
  lines.push(`Current floor:            ${money(d.floor)}`);
  lines.push(`Producer premium:         ${money(d.premium)}/cwt`);
  lines.push(`Net floor:                ${money(d.netFloor)}`);
  // ⚠ QUOTE THE SAMPLE THE RANK WAS ACTUALLY TAKEN OVER. This printed `d.n`,
  // the whole captured history, while the page printed the matched-horizon
  // band — so the same cell read n=175 in the email and n=19 on screen. Two
  // numbers for one sample is the shape this repo keeps paying for.
  const sampleN = d.pctileBasis === "horizon-matched" && d.horizonBandN != null ? d.horizonBandN : d.n;
  const sampleTxt = d.pctileBasis === "horizon-matched" && d.horizonBandMonths != null
    ? `n=${sampleN} within ±${d.horizonBandMonths}mo of ${d.monthsOut}mo out`
    : `n=${sampleN}, life-to-date`;
  lines.push(`Historical position:      ${ord(d.pctile)} percentile (${sampleTxt})`);
  if (d.pctileBasis === "horizon-adjusted" && d.rawPctile != null
      && Math.abs(d.rawPctile - d.pctile) >= 0.05) {
    lines.push(`                          ${ord(d.rawPctile)} unadjusted — the gap is the horizon effect`);
  }

  // ── the named absences ──────────────────────────────────────────────────
  const fit = d.farmFit && d.farmFit.state === "NOT_EVALUATED"
    ? "not evaluated — no farm economics in this layer"
    : (d.farmFit && d.farmFit.summary) || "not evaluated";
  lines.push(`Farm margin fit:          ${fit}`);

  // ⚠ THIS ALERT NAMES NO FARM, SO IT MAKES NO COVERAGE CLAIM.
  // It used to print "Existing quarter coverage: incomplete / none on file",
  // which reads as a statement about a dairy. There is no dairy here — only the
  // agency's own book. Report that count and say what it is; a farm's actual
  // exposure is Farm Fit, which is not built.
  // ⚠ A POLICY IS NOT A DAIRY. Measured 2026-09-03: 6 of the 9 DRP farmers hold
  // more than one DRP policy, so a distinct-`policy_id` count overstates the
  // dairies — Q4 2026 read 6 against 5. `unit` says which the caller could
  // actually resolve, and the sentence names it rather than assuming.
  const bk = coverage && coverage.book;
  const holders = bk ? (bk.holders != null ? bk.holders : 0) : 0;
  const unitTxt = !bk ? ""
    : bk.unit === "dairy" ? `${holders} dair${holders === 1 ? "y" : "ies"}`
    : `${holders} polic${holders === 1 ? "y" : "ies"}`;
  const cov = !bk ? "not loaded"
    : bk.n === 0 ? "nothing on this quarter yet"
    : `${bk.n} endorsement${bk.n === 1 ? "" : "s"} across ${unitTxt}`;
  lines.push(`Agency book, this quarter: ${cov}`);
  lines.push(`                          (book-wide count, not this or any one dairy)`);

  lines.push(`Sales deadline:           not held — no verified RMA sales-window source`);
  lines.push(`Data quality:             ${d.dataQuality ? d.dataQuality.status : "—"} · ${d.usability || "—"}`);
  lines.push(`Board:                    ${d.boardDate || "—"} (${d.boardHealth ? d.boardHealth.state : "—"})`);
  lines.push("");
  lines.push("This is a prompt to review the quarter, not a recommendation to purchase.");
  lines.push("Quoted figures are the board convention: 95% coverage, protection factor 1.0.");
  lines.push("Review DRP →");

  return {
    subject,
    text: lines.join("\n"),
    // The rendered body carries no instruction to buy. Asserted by the checker
    // rather than trusted, because this is the one surface where the wrong verb
    // reaches a person.
    kind: transition.kind,
  };
}

module.exports = { renderDrpAlert, prettyQuarter };
